import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight,
  Check,
  Linkedin,
  Sparkles,
  Lock,
  Target,
  AlertCircle,
  Zap,
  X,
  BookOpen,
  FileText,
  Download,
  ShieldCheck,
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { trackVisit, createOrder, trackCtaClick } from "../lib/api";
import {
  trackClickButton,
  trackAddToCart,
  trackInitiateCheckout,
  trackViewContent,
} from "../lib/tiktok";
import { toast } from "sonner";

// ─────────────────────────────────────────────────────────────────────────
// AI Work Upgrade — USA pivot (English, LTR).
// Instructor: Jacqueline &quot;Jackie&quot; Escalera · Data & AI Consultant @ Microsoft
// Launch price $49 / Anchor $129. CTAs route to the INTERNAL Stripe Elements
// checkout (/checkout/{id}) — the same flow we hardened after the React
// StrictMode setClientSecret bug fix.
// ─────────────────────────────────────────────────────────────────────────

const LINKEDIN_URL = "https://www.linkedin.com/in/jackieescalera";
const LAUNCH_PRICE = 49;
const ANCHOR_PRICE = 129;
const PRICE_LABEL = `$${LAUNCH_PRICE}`;
const ANCHOR_LABEL = `$${ANCHOR_PRICE}`;
// UAE-targeted secondary price — approximate AED so buyers can mental-math it.
// $49 × ~3.67 ≈ AED 180. Update both values together if launch price changes.
const PRICE_LABEL_AED = "AED 180";

// Fade-up wrapper (intersection-observer, runs once).
function FadeUp({ children, delay = 0, className = "" }) {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setShown(true);
          io.disconnect();
        }
      },
      { threshold: 0.12 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: shown ? 1 : 0,
        transform: shown ? "translateY(0)" : "translateY(16px)",
        transition: `opacity 700ms ease-out ${delay}ms, transform 700ms ease-out ${delay}ms`,
        willChange: "opacity, transform",
      }}
    >
      {children}
    </div>
  );
}

// Curriculum modules — copy lifted verbatim from the product brief.
const MODULES = [
  {
    n: "01",
    title: "Your Manager Asks for Your Opinion on an Important Decision",
    scenario:
      'Your manager asks: "What do you think we should do?" Most people answer too quickly. They give an opinion. But a stronger professional uses AI to structure the decision before responding.',
    learn: [
      "A clear point of view",
      "Key assumptions",
      "Potential risks",
      "Alternative options",
      "Smart questions",
      "A professional recommendation",
    ],
    framework: "Decision Response System",
    output:
      'A manager-ready response: "My initial recommendation is to move forward, but before making the final call, I would pressure-test three areas…"',
    why: "This helps you stop sounding like someone with a quick opinion and start sounding like someone with structured judgment.",
  },
  {
    n: "02",
    title: "You Have an Important Meeting Tomorrow",
    scenario:
      "Vague invite. Unclear agenda. Discussion that may matter. Most people walk into meetings and react. Stronger professionals walk in prepared.",
    learn: [
      "Key questions",
      "Expected objections",
      "Likely decisions",
      "Stakeholder priorities",
      "Risks to watch",
      "An initial recommendation",
    ],
    framework: "Meeting Advantage System",
    output: "A Meeting Prep Sheet you can use before important meetings.",
    why: "Prepared people stand out. Not because they talk more, but because they ask sharper questions and anticipate what others miss.",
  },
  {
    n: "03",
    title: "Your Manager Asks for a Report",
    scenario:
      "You have data, notes, campaign results, sales numbers, or project updates. Most people summarize what happened. But managers do not need more summaries — they need insight.",
    learn: [
      "What happened",
      "Why it happened",
      "What it means",
      "What risks exist",
      "What should happen next",
    ],
    framework: "Insight Report System",
    output: "Executive Summary + Recommendations",
    why: "A weak report says what happened. A strong report helps people decide what to do next.",
  },
  {
    n: "04",
    title: "You Need to Reply to a Sensitive Email",
    scenario:
      "A manager, client, or stakeholder sends a difficult message. Maybe pressure, a complaint, a delay, or something that went wrong. Most people respond too quickly and sound defensive, apologetic, vague, or tense.",
    learn: [
      "The real concern",
      "What needs to be acknowledged",
      "What needs to be clarified",
      "What should not be over-explained",
      "The next professional step",
    ],
    framework: "Calm Reply System",
    output:
      "Three ready-to-use response styles: diplomatic · firm · executive concise.",
    why: "Sensitive replies are career moments. The goal is not to sound nice — it is to sound clear, calm, responsible, and professional.",
  },
  {
    n: "05",
    title: "Before You Send Any Important Work",
    scenario:
      "You finish a report, presentation, email, proposal, or recommendation. Most people review grammar and formatting. The real risk is not a typo — it is weak logic, missing context, unsupported assumptions, or unclear recommendations.",
    learn: [
      "Logic",
      "Clarity",
      "Gaps",
      "Assumptions",
      "Risks",
      "Actionability",
      "Strength of recommendation",
    ],
    framework: "Pre-Send Review System",
    output: "AI Work Checklist",
    why: "This is the habit that improves every piece of work you send. It helps you catch weaknesses before your manager, client, or team does.",
  },
];

const BONUSES = [
  {
    title: "50-Prompt AI Work Library",
    desc:
      "A practical prompt library organized by real work situations: decisions, meetings, reports, sensitive emails, work reviews, presentations, interviews, and manager/client issues. Not a random list — a structured library you can use when work gets unclear, urgent, or high-stakes.",
  },
  {
    title: "AI Work Templates",
    desc:
      "Ready-to-use templates for Decision Memo · Meeting Prep Sheet · Executive Summary · Sensitive Email Reply · Pre-Send Review Checklist.",
  },
  {
    title: "Before / After Examples",
    desc:
      "See the difference between generic AI output and work improved through the AI Work Upgrade systems. Before: surface-level, easy to ignore. After: structured, specific, decision-ready, and more professional.",
  },
];

const CONTRAST_CARDS = [
  {
    not: "Use AI to write faster",
    instead: "Use AI to think more clearly before you respond.",
  },
  {
    not: "Get more prompts",
    instead:
      "Use structured systems for decisions, meetings, reports, emails, and reviews.",
  },
  {
    not: "Learn AI tools",
    instead:
      "Apply AI to the work moments that shape your career reputation.",
  },
];

const FAQS = [
  {
    q: "Do I need to be technical?",
    a: "No. This course is built for professionals, not engineers.",
  },
  {
    q: "Do I need ChatGPT Plus?",
    a: "No. You can apply the systems using any capable AI assistant including ChatGPT, Claude, Gemini, or similar tools.",
  },
  {
    q: "Is this just a prompt pack?",
    a: "No. The prompt library is included, but the core value is the workflow behind each work situation.",
  },
  {
    q: "How is this different from free AI content online?",
    a: "Free content usually teaches isolated prompts or tool tips. This course is built around real work scenarios and shows you how to think through them using AI.",
  },
  {
    q: "How do I access the course?",
    a: "After purchase, you receive instant digital access to the course materials.",
  },
  {
    q: "Is this useful outside marketing?",
    a: "Yes. The course is designed for general professional work: decisions, meetings, reports, emails, and work reviews.",
  },
];

export default function LandingAI() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [stickyVisible, setStickyVisible] = useState(false);
  const onMountTracked = useRef(false);

  useEffect(() => {
    if (onMountTracked.current) return;
    onMountTracked.current = true;
    trackVisit("landing-ai");
    trackViewContent("ai_work_upgrade");
    const prev = document.title;
    document.title = "AI Work Upgrade — Practical AI for Working Professionals | Jackie Escalera";
    return () => {
      document.title = prev;
    };
  }, []);

  // Show the sticky CTA bar after the user has scrolled past the hero
  // (~600px). Mobile already has a permanent sticky CTA; this adds the
  // slim desktop variant requested for higher conversion.
  useEffect(() => {
    let raf = 0;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        setStickyVisible(window.scrollY > 600);
        raf = 0;
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  // Route to the INTERNAL Stripe Elements checkout (not the hosted Payment Link).
  // This is the same flow we shipped after the React StrictMode cancelled-flag
  // bug fix in StripeCheckoutPanel.jsx.
  const startCheckout = async (source = "primary") => {
    if (loading) return;
    setLoading(true);
    try {
      trackClickButton(`ai-${source}`);
      trackCtaClick(`buy-ai-${source}`, "landing-ai");
      trackAddToCart("ai_work_upgrade");
      trackInitiateCheckout("ai_work_upgrade");
      const order = await createOrder("ai_work_upgrade");
      navigate(`/checkout/${order.order_id}`);
    } catch (e) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div
      className="min-h-screen bg-white text-[#111827]"
      dir="ltr"
      lang="en"
      data-testid="landing-ai-root"
      style={{
        fontFamily:
          "'Inter','SF Pro Display',system-ui,-apple-system,'Segoe UI',sans-serif",
      }}
    >
      {/* Minimal header */}
      <header className="border-b border-[#E5E7EB] bg-white/95 backdrop-blur-sm sticky top-0 z-30" data-testid="ai-header">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-md bg-[#0F172A] flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-[#C9A227]" strokeWidth={2} />
            </div>
            <div className="leading-tight">
              <div className="font-bold text-[15px] text-[#0F172A]">AI Work Upgrade</div>
              <div className="text-[10px] text-[#6B7280] tracking-wide">Executive Education</div>
            </div>
          </div>
          <a
            href={LINKEDIN_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackCtaClick("ai-header-linkedin", "landing-ai")}
            className="text-xs sm:text-sm text-[#6B7280] hover:text-[#0F172A] flex items-center gap-1.5 transition-colors"
            data-testid="ai-header-linkedin"
          >
            <Linkedin className="w-4 h-4" />
            <span className="hidden sm:inline">Jackie Escalera</span>
          </a>
        </div>
      </header>

      {/* HERO */}
      <section className="relative pt-12 sm:pt-20 pb-14 sm:pb-20 overflow-hidden" data-testid="ai-hero">
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none opacity-[0.03]"
          style={{
            backgroundImage:
              "linear-gradient(#0F172A 1px,transparent 1px),linear-gradient(90deg,#0F172A 1px,transparent 1px)",
            backgroundSize: "44px 44px",
          }}
        />
        <div className="relative max-w-6xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-10 lg:gap-12 items-center">
          <FadeUp className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#0F172A]/5 border border-[#0F172A]/10 text-[11px] text-[#0F172A] mb-6 tracking-wide font-semibold">
              <Sparkles className="w-3 h-3 text-[#C9A227]" />
              For working professionals · UAE &amp; international
            </div>
            <h1 className="font-black text-3xl sm:text-5xl lg:text-[3.4rem] leading-[1.12] text-[#0F172A] tracking-tight">
              Use AI to <span className="text-[#C9A227]">Stand Out</span> in a Fast-Moving
              <br />
              Professional Market.
            </h1>
            <p className="mt-6 text-base sm:text-lg text-[#374151] leading-relaxed max-w-2xl">
              AI Work Upgrade helps professionals in the UAE use AI to prepare sharper
              reports, emails, meeting notes, recommendations, and client-ready work —
              without sounding generic.
            </p>
            <p className="mt-4 text-[13px] sm:text-sm text-[#6B7280] leading-relaxed max-w-2xl" data-testid="ai-built-for-line">
              <span className="font-semibold text-[#0F172A]">Built for busy professionals</span> —
              managers, consultants, analysts, marketers, and team leads who need to
              communicate clearly and move fast.
            </p>

            {/* Instructor credibility line */}
            <div className="mt-5 inline-flex items-center gap-2 text-sm text-[#374151]">
              <span className="text-[#6B7280]">Taught by</span>
              <span className="font-bold text-[#0F172A]">Jacqueline &quot;Jackie&quot; Escalera</span>
              <span className="text-[#6B7280]">·</span>
              <span className="text-[#0F172A]">Data &amp; AI Consultant</span>
            </div>

            {/* Trust bullets — 5 items, UAE-targeted "system/workflow" language */}
            <ul className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-2.5 max-w-xl" data-testid="ai-trust-row">
              {[
                "5 practical workplace workflows",
                "50 ready-to-use AI prompts",
                "Templates for meetings, reports, emails",
                "Instant digital access — one-time payment",
                "No technical background required",
              ].map((t) => (
                <li key={t} className="flex items-center gap-2 text-sm text-[#374151]">
                  <span className="w-5 h-5 rounded-full bg-[#0F766E]/10 flex items-center justify-center shrink-0">
                    <Check className="w-3 h-3 text-[#0F766E]" strokeWidth={3} />
                  </span>
                  {t}
                </li>
              ))}
            </ul>

            <div className="mt-7 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              <button
                onClick={() => { trackCtaClick("ai-hero-see-inside", "landing-ai"); scrollTo("ai-what-you-get"); }}
                data-testid="ai-hero-see-inside-btn"
                className="bg-[#0F172A] hover:bg-[#1E293B] text-white font-bold text-base sm:text-[15px] px-8 py-4 rounded-md transition-all shadow-[0_8px_24px_rgba(15,23,42,0.25)] hover:shadow-[0_12px_32px_rgba(15,23,42,0.35)] flex items-center justify-center gap-2"
              >
                <span>See What&apos;s Inside</span>
                <ArrowRight className="w-4 h-4" />
              </button>
              <div className="flex flex-col gap-0.5 px-2 self-center">
                <div className="flex items-baseline gap-2">
                  <span className="text-[#6B7280] text-sm line-through">Reg {ANCHOR_LABEL}</span>
                  <span className="text-[#C9A227] font-black text-2xl leading-none">{PRICE_LABEL}</span>
                  <span className="bg-[#C9A227]/10 border border-[#C9A227]/40 text-[#C9A227] text-[10px] font-black px-1.5 py-0.5 rounded uppercase tracking-wider">launch</span>
                </div>
                <span className="text-[11px] text-[#6B7280]" data-testid="hero-aed-price">Approx. {PRICE_LABEL_AED}</span>
              </div>
            </div>

            {/* Reassurance line under CTA */}
            <div className="mt-4 flex items-center gap-2 text-[12px] text-[#6B7280]">
              <Lock className="w-3 h-3 text-[#0F766E]" />
              <span><span className="font-semibold text-[#0F172A]">14-day money-back guarantee.</span> Instant access. Self-paced.</span>
            </div>
          </FadeUp>

          {/* HERO VISUAL — premium photo, lazy-rendered with explicit dimensions
              to prevent CLS. On mobile this appears below the headline+CTA so
              the CTA stays above the fold on phones. */}
          <FadeUp delay={120} className="lg:col-span-5 order-first lg:order-none">
            <div className="relative max-w-md mx-auto lg:max-w-none">
              <div
                aria-hidden
                className="absolute -inset-3 rounded-2xl bg-gradient-to-br from-[#C9A227]/15 to-[#0F172A]/8 blur-2xl opacity-70"
              />
              <img
                src="https://customer-assets.emergentagent.com/job_charisma-protocol/artifacts/cxntxzu6_ChatGPT%20Image%20Jun%2010%2C%202026%2C%2004_34_54%20AM.png"
                alt="A professional reviewing AI-assisted work in a modern executive office — laptop on a polished desk with charts and notes."
                width="1024"
                height="1024"
                loading="eager"
                decoding="async"
                fetchPriority="high"
                className="relative w-full h-auto rounded-2xl border border-[#E5E7EB] shadow-[0_25px_70px_-25px_rgba(15,23,42,0.45)] object-cover"
                data-testid="ai-hero-image"
              />
            </div>
          </FadeUp>
        </div>
      </section>

      {/* BUILT FOR REAL WORKPLACE SITUATIONS — UAE-relevant, tangible scenarios */}
      <section className="py-14 sm:py-20 bg-[#F9FAFB]" data-testid="ai-workplace-situations">
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <FadeUp>
            <div className="text-center mb-10 sm:mb-12">
              <div className="text-[11px] text-[#C9A227] tracking-[0.3em] font-bold mb-3">REAL WORK · NOT THEORY</div>
              <h2 className="font-black text-2xl sm:text-4xl text-[#0F172A] leading-tight">
                Built for Real Workplace Situations
              </h2>
              <p className="mt-4 text-[#6B7280] max-w-2xl mx-auto text-[15px]">
                Workflows you can use the same day — not abstract AI theory.
              </p>
            </div>
          </FadeUp>
          <div className="grid sm:grid-cols-2 gap-4 sm:gap-5">
            {[
              {
                title: "Before a meeting",
                body: "Walk in with sharper questions, talking points, and risks to watch.",
              },
              {
                title: "Before sending a report",
                body: "Turn updates into clearer insights and recommendations.",
              },
              {
                title: "Before replying to a sensitive email",
                body: "Respond calmly without sounding vague or defensive.",
              },
              {
                title: "Before sending work to a manager or client",
                body: "Let AI review logic, gaps, and clarity first.",
              },
            ].map((c, i) => (
              <div
                key={i}
                className="bg-white border border-[#E5E7EB] rounded-xl p-5 sm:p-6 hover:border-[#C9A227]/40 hover:shadow-[0_10px_30px_-15px_rgba(15,23,42,0.15)] transition-all"
                data-testid={`ai-situation-card-${i}`}
              >
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-lg bg-[#0F172A]/5 border border-[#0F172A]/10 flex items-center justify-center shrink-0">
                    <Check className="w-4 h-4 text-[#0F766E]" strokeWidth={3} />
                  </div>
                  <div>
                    <div className="font-bold text-[#0F172A] text-[15px] sm:text-base leading-tight">{c.title}</div>
                    <div className="text-[#374151] text-[14px] mt-1.5 leading-relaxed">{c.body}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHAT YOU GET TODAY — fast value snapshot above the fold scroll line */}
      <section id="ai-what-you-get" className="py-14 sm:py-20 bg-[#0F172A] text-white" data-testid="ai-what-you-get">
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <FadeUp>
            <div className="text-center mb-10 sm:mb-12">
              <div className="text-[11px] text-[#C9A227] tracking-[0.3em] font-bold mb-3">WHAT YOU GET TODAY</div>
              <h2 className="font-black text-2xl sm:text-4xl leading-tight">
                Everything inside — for <span className="text-[#C9A227]">{PRICE_LABEL}</span>
              </h2>
              <div className="mt-2 text-[13px] text-white/55" data-testid="ai-what-aed">Approx. {PRICE_LABEL_AED} · one-time payment · no subscription</div>
            </div>
          </FadeUp>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4" data-testid="ai-what-grid">
            {[
              { icon: BookOpen, n: "5", label: "Workplace workflows", sub: "Decision · Meeting · Report · Email · Review" },
              { icon: Sparkles, n: "50", label: "AI prompts", sub: "Copy-paste ready" },
              { icon: FileText, n: "5+", label: "Work templates", sub: "Decision memo · Meeting prep · Exec summary · Reply · Pre-send" },
              { icon: ArrowRight, n: "12", label: "Before/after examples", sub: "Generic AI → senior-quality output" },
              { icon: Download, n: "", label: "Instant access", sub: "Direct download link · Lifetime access" },
            ].map((b, i) => {
              const Icon = b.icon;
              return (
                <div
                  key={i}
                  className="bg-white/5 border border-white/10 hover:border-[#C9A227]/40 rounded-xl p-5 transition-colors"
                  data-testid={`ai-what-card-${i}`}
                >
                  <div className="flex items-baseline gap-2 mb-2">
                    {b.n && <div className="text-[#C9A227] font-black text-3xl leading-none">{b.n}</div>}
                    <Icon className={`text-[#C9A227] ${b.n ? "w-4 h-4" : "w-7 h-7"}`} strokeWidth={2} />
                  </div>
                  <div className="font-bold text-[14px] text-white leading-tight">{b.label}</div>
                  <div className="text-[11px] text-white/55 mt-1 leading-snug">{b.sub}</div>
                </div>
              );
            })}
          </div>

          {/* What happens after payment? — UAE buyers need to know exactly what they get instantly. */}
          <div className="mt-10 max-w-3xl mx-auto bg-white/[0.04] border border-[#C9A227]/30 rounded-xl p-6 sm:p-7" data-testid="ai-after-payment-box">
            <div className="text-[11px] text-[#C9A227] tracking-[0.25em] font-bold mb-3">WHAT HAPPENS AFTER PAYMENT</div>
            <ul className="space-y-2.5 text-[14px] sm:text-[15px] text-white/90 leading-relaxed">
              {[
                "You get instant access to AI Work Upgrade",
                "You receive the full course materials",
                "You get the 50-prompt AI Work Library",
                "You get work templates for meetings, reports, emails, and decisions",
                "You can start using the system immediately",
              ].map((line, i) => (
                <li key={i} className="flex items-start gap-2.5">
                  <Check className="w-4 h-4 text-[#5EEAD4] mt-1 shrink-0" strokeWidth={3} />
                  <span>{line}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="mt-8 text-center">
            <button
              onClick={() => startCheckout("what-you-get")}
              disabled={loading}
              data-testid="ai-what-buy-btn"
              className="bg-[#C9A227] hover:bg-[#b78d1b] text-[#0F172A] font-black text-base px-8 py-4 rounded-md transition-colors disabled:opacity-50 inline-flex items-center gap-2"
            >
              Get Instant Access — {PRICE_LABEL}
              <ArrowRight className="w-4 h-4" />
            </button>
            <div className="mt-3 text-[11px] text-white/55 leading-relaxed max-w-md mx-auto">
              <div className="flex items-center justify-center gap-1.5">
                <ShieldCheck className="w-3 h-3 text-[#5EEAD4]" />
                14-day money-back guarantee · One-time payment, no subscription
              </div>
              <div className="mt-1.5 text-white/45">
                Secure checkout. Pay by card, Apple Pay, Google Pay or supported wallet methods.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PRODUCT PREVIEW — tangible glimpse of prompts / templates / before-after */}
      <section className="py-16 sm:py-24" data-testid="ai-preview">
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <FadeUp className="text-center mb-12">
            <div className="text-[11px] text-[#C9A227] tracking-[0.3em] font-bold mb-3">INSIDE THE COURSE</div>
            <h2 className="font-black text-2xl sm:text-4xl text-[#0F172A] leading-tight">
              Here&apos;s exactly what you&apos;ll be using on day one
            </h2>
          </FadeUp>

          <div className="grid lg:grid-cols-3 gap-5 sm:gap-6">
            {/* Preview 1 — Prompt card */}
            <FadeUp delay={60}>
              <div className="bg-[#F9FAFB] border border-[#E5E7EB] rounded-xl overflow-hidden h-full" data-testid="ai-preview-prompt">
                <div className="bg-white border-b border-[#E5E7EB] px-5 py-3 flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-[#C9A227]" />
                  <span className="text-[11px] font-bold text-[#0F172A] tracking-wider uppercase">Prompt #14 · Sensitive Reply</span>
                </div>
                <div className="p-5 text-[13px] text-[#374151] font-mono leading-relaxed">
                  <span className="text-[#C9A227]">You are</span> a senior advisor.
                  My manager sent the email below.
                  Identify the real concern, then draft a calm reply that:<br />
                  <span className="text-[#0F766E]">1)</span> acknowledges without over-apologising,<br />
                  <span className="text-[#0F766E]">2)</span> clarifies without sounding defensive,<br />
                  <span className="text-[#0F766E]">3)</span> proposes one clear next step.<br /><br />
                  <span className="text-[#9CA3AF]">[paste the email here]</span>
                </div>
                <div className="px-5 py-3 bg-white border-t border-[#E5E7EB] text-[11px] text-[#6B7280]">
                  1 of 50 ready-to-use prompts
                </div>
              </div>
            </FadeUp>

            {/* Preview 2 — Template */}
            <FadeUp delay={120}>
              <div className="bg-[#F9FAFB] border border-[#E5E7EB] rounded-xl overflow-hidden h-full" data-testid="ai-preview-template">
                <div className="bg-white border-b border-[#E5E7EB] px-5 py-3 flex items-center gap-2">
                  <FileText className="w-4 h-4 text-[#C9A227]" />
                  <span className="text-[11px] font-bold text-[#0F172A] tracking-wider uppercase">Template · Decision Memo</span>
                </div>
                <div className="p-5 text-[13px] text-[#374151] leading-relaxed space-y-2">
                  <div><span className="font-bold text-[#0F172A]">Decision:</span> <span className="text-[#9CA3AF]">[one sentence]</span></div>
                  <div><span className="font-bold text-[#0F172A]">Assumptions:</span> 3 max</div>
                  <div><span className="font-bold text-[#0F172A]">Alternatives considered:</span> A / B / C</div>
                  <div><span className="font-bold text-[#0F172A]">Risks &amp; mitigations:</span></div>
                  <div><span className="font-bold text-[#0F172A]">Recommendation:</span> <span className="text-[#9CA3AF]">why now, why this</span></div>
                  <div><span className="font-bold text-[#0F172A]">What I need from you:</span></div>
                </div>
                <div className="px-5 py-3 bg-white border-t border-[#E5E7EB] text-[11px] text-[#6B7280]">
                  1 of 5+ structured work templates
                </div>
              </div>
            </FadeUp>

            {/* Preview 3 — Before/After */}
            <FadeUp delay={180}>
              <div className="bg-[#F9FAFB] border border-[#E5E7EB] rounded-xl overflow-hidden h-full" data-testid="ai-preview-beforeafter">
                <div className="bg-white border-b border-[#E5E7EB] px-5 py-3 flex items-center gap-2">
                  <ArrowRight className="w-4 h-4 text-[#C9A227]" />
                  <span className="text-[11px] font-bold text-[#0F172A] tracking-wider uppercase">Before / After</span>
                </div>
                <div className="divide-y divide-[#E5E7EB]">
                  <div className="p-5">
                    <div className="text-[10px] text-[#9CA3AF] tracking-widest font-bold mb-2">GENERIC AI</div>
                    <p className="text-[13px] text-[#6B7280] italic leading-relaxed line-through decoration-[#F87171]/40">
                      &quot;Q3 sales were 8% higher than Q2. The team performed well. We should keep up the good work next quarter.&quot;
                    </p>
                  </div>
                  <div className="p-5 bg-white">
                    <div className="text-[10px] text-[#0F766E] tracking-widest font-bold mb-2">AFTER UPGRADE</div>
                    <p className="text-[13px] text-[#0F172A] leading-relaxed">
                      &quot;Q3 grew 8% — driven by 2 accounts. Without them, growth is flat. Recommend doubling outreach to similar accounts in Q4; risk if those 2 churn.&quot;
                    </p>
                  </div>
                </div>
                <div className="px-5 py-3 bg-white border-t border-[#E5E7EB] text-[11px] text-[#6B7280]">
                  1 of 12 worked examples
                </div>
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-24 bg-[#F9FAFB] border-y border-[#E5E7EB]" data-testid="ai-problem">
        <div className="max-w-4xl mx-auto px-5 sm:px-8">
          <FadeUp>
            <div className="text-xs text-[#C9A227] tracking-[0.3em] font-bold mb-3">THE GAP</div>
            <h2 className="font-black text-2xl sm:text-4xl text-[#0F172A] mb-6 leading-snug">
              Most Professionals Are Using AI the Wrong Way
            </h2>
            <p className="text-base sm:text-lg text-[#374151] leading-relaxed mb-4">
              Most people use AI to write faster. They ask it to draft an email, summarize a document, or
              rewrite a paragraph. That helps. But it is not where the real advantage is.
            </p>
            <p className="text-base sm:text-lg text-[#374151] leading-relaxed mb-4">
              The real advantage is using AI to improve how you <span className="font-bold text-[#0F172A]">think</span> before you speak, decide, present,
              respond, or send work.
            </p>
            <p className="text-base sm:text-lg text-[#374151] leading-relaxed mb-10">
              Because at work, you are not judged by how fast you type. You are judged by the quality of your thinking.
            </p>

            <div className="grid sm:grid-cols-2 gap-6">
              <div className="bg-white rounded-xl border border-[#E5E7EB] p-6">
                <div className="text-xs text-[#B91C1C] tracking-[0.2em] font-bold mb-3 flex items-center gap-2">
                  <X className="w-3.5 h-3.5" /> MOST AI USERS STOP AT
                </div>
                <ul className="space-y-2 text-sm text-[#374151]">
                  {[
                    '"Write this for me"',
                    '"Summarize this"',
                    '"Make this sound better"',
                    '"Give me ideas"',
                  ].map((t) => (
                    <li key={t} className="flex items-start gap-2">
                      <span className="mt-1.5 w-1 h-1 rounded-full bg-[#B91C1C] shrink-0" />
                      {t}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-white rounded-xl border border-[#0F766E]/30 p-6">
                <div className="text-xs text-[#0F766E] tracking-[0.2em] font-bold mb-3 flex items-center gap-2">
                  <Check className="w-3.5 h-3.5" /> THIS COURSE TEACHES YOU TO
                </div>
                <ul className="space-y-2 text-sm text-[#0F172A]">
                  {[
                    "Pressure-test decisions",
                    "Prepare for high-stakes meetings",
                    "Turn reports into insights",
                    "Respond to sensitive emails professionally",
                    "Review your work like a strict manager before anyone else sees it",
                  ].map((t) => (
                    <li key={t} className="flex items-start gap-2">
                      <Check className="w-3.5 h-3.5 mt-0.5 text-[#0F766E] shrink-0" strokeWidth={3} />
                      {t}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* POSITIONING */}
      <section className="py-16 sm:py-24" data-testid="ai-positioning">
        <div className="max-w-5xl mx-auto px-5 sm:px-8">
          <FadeUp>
            <div className="text-xs text-[#C9A227] tracking-[0.3em] font-bold mb-3">THE DIFFERENCE</div>
            <h2 className="font-black text-2xl sm:text-4xl text-[#0F172A] mb-6 leading-snug">
              This Is Not Another ChatGPT Course
            </h2>
            <p className="text-base sm:text-lg text-[#374151] leading-relaxed mb-2 max-w-3xl">
              This course does not teach generic prompts. It does not teach tool features. It does not teach theory.
            </p>
            <p className="text-base sm:text-lg text-[#374151] leading-relaxed mb-12 max-w-3xl">
              It teaches practical AI workflows for real work situations — the kind of situations where your
              output affects how professional, prepared, and strategic you appear.
            </p>
          </FadeUp>

          <div className="grid md:grid-cols-3 gap-5">
            {CONTRAST_CARDS.map((c, i) => (
              <FadeUp key={i} delay={i * 90}>
                <div className="bg-white border border-[#E5E7EB] rounded-xl p-6 h-full">
                  <div className="flex items-center gap-2 text-[10px] text-[#B91C1C] tracking-[0.2em] font-bold mb-3">
                    <X className="w-3 h-3" /> NOT
                  </div>
                  <p className="text-sm text-[#6B7280] mb-5 italic">&quot;{c.not}&quot;</p>
                  <div className="flex items-center gap-2 text-[10px] text-[#0F766E] tracking-[0.2em] font-bold mb-3">
                    <Check className="w-3 h-3" strokeWidth={3} /> INSTEAD
                  </div>
                  <p className="text-[15px] text-[#0F172A] font-semibold leading-snug">{c.instead}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* INSTRUCTOR */}
      <section className="py-16 sm:py-24 bg-[#F9FAFB] border-y border-[#E5E7EB]" data-testid="ai-instructor">
        <div className="max-w-5xl mx-auto px-5 sm:px-8">
          <FadeUp>
            <div className="text-xs text-[#C9A227] tracking-[0.3em] font-bold mb-3">THE INSTRUCTOR</div>
            <h2 className="font-black text-2xl sm:text-4xl text-[#0F172A] mb-10 leading-snug">
              Learn From Someone Who Works With AI in the Real World
            </h2>
            <div className="grid md:grid-cols-3 gap-8 items-start">
              <div className="md:col-span-1">
                {/* Headshot placeholder — CIRCULAR. Replace src with Jackie's
                    real headshot URL when delivered. Keep dimensions identical
                    to avoid layout shift on swap. */}
                <div className="relative w-[200px] h-[200px] mx-auto md:mx-0 rounded-full bg-gradient-to-br from-[#0F172A] to-[#1E293B] flex items-center justify-center overflow-hidden border-4 border-white shadow-[0_20px_50px_-20px_rgba(15,23,42,0.4)]">
                  <div className="text-center">
                    <div className="text-6xl font-black text-[#C9A227]" data-testid="ai-instructor-initials">JE</div>
                    <div className="text-[9px] text-white/55 tracking-[0.3em] mt-1">JACKIE ESCALERA</div>
                  </div>
                  {/* Tiny corner note so the team remembers to swap this image */}
                  <span
                    aria-hidden
                    className="absolute bottom-1 right-1 text-[8px] text-white/30 tracking-wider uppercase"
                  >
                    photo placeholder
                  </span>
                </div>
                <a
                  href={LINKEDIN_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackCtaClick("ai-instructor-linkedin", "landing-ai")}
                  data-testid="ai-instructor-linkedin"
                  className="mt-5 max-w-[220px] mx-auto md:mx-0 inline-flex items-center justify-center gap-2 text-[#0077B5] hover:text-[#005f8e] border border-[#0077B5]/30 hover:border-[#0077B5] font-semibold text-sm py-2.5 px-4 rounded-md transition-colors w-full"
                >
                  <Linkedin className="w-4 h-4" />
                  View Jackie&apos;s LinkedIn Profile
                </a>
              </div>
              <div className="md:col-span-2 text-[#374151] leading-relaxed space-y-4 text-base sm:text-[17px]">
                <div className="space-y-1">
                  <div className="font-black text-xl text-[#0F172A]">Jacqueline &quot;Jackie&quot; Escalera</div>
                  <div className="text-sm text-[#0077B5] font-semibold">Data &amp; AI Consultant</div>
                </div>
                <p>
                  Jackie works at the intersection of data, AI, and practical business problem-solving.
                  AI Work Upgrade was created to help professionals move beyond basic prompts and use AI as
                  a structured thinking partner for real workplace situations.
                </p>
                <blockquote className="border-l-4 border-[#C9A227] pl-5 py-1 italic text-[#0F172A] font-medium" data-testid="ai-instructor-quote">
                  &quot;AI should not just help you produce more work. It should help you produce clearer,
                  sharper, more useful work.&quot;
                  <footer className="not-italic font-bold text-[13px] text-[#6B7280] mt-2">— Jackie Escalera</footer>
                </blockquote>
              </div>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* WHAT MAKES THIS DIFFERENT */}
      <section className="py-16 sm:py-24" data-testid="ai-different">
        <div className="max-w-4xl mx-auto px-5 sm:px-8">
          <FadeUp>
            <div className="text-xs text-[#C9A227] tracking-[0.3em] font-bold mb-3">STRUCTURE</div>
            <h2 className="font-black text-2xl sm:text-4xl text-[#0F172A] mb-6 leading-snug">
              What Makes AI Work Upgrade Different?
            </h2>
            <p className="text-base sm:text-lg text-[#374151] leading-relaxed mb-4">
              Most AI courses are organized around <span className="font-bold text-[#0F172A]">tools.</span>
              {" "}This course is organized around <span className="font-bold text-[#0F172A]">work situations.</span>
            </p>
            <p className="text-base sm:text-lg text-[#374151] leading-relaxed mb-3">Because professionals do not wake up thinking:</p>
            <p className="text-[#6B7280] italic mb-6 pl-5 border-l-2 border-[#E5E7EB]">&quot;I need to learn another AI feature.&quot;</p>
            <p className="text-base sm:text-lg text-[#374151] leading-relaxed mb-3">They think:</p>
            <ul className="space-y-2 pl-5 border-l-2 border-[#C9A227] mb-8 text-[#0F172A]">
              {[
                '"I have a meeting tomorrow."',
                '"I need to send this report."',
                '"My manager asked for my opinion."',
                '"A client sent a difficult email."',
                '"I need this work to be stronger before I send it."',
              ].map((t) => <li key={t} className="italic">{t}</li>)}
            </ul>
            <p className="text-base sm:text-lg text-[#374151] leading-relaxed mb-5">
              That is exactly how this course is structured. Every module follows the same practical format:
            </p>
            <div className="flex flex-wrap gap-2">
              {["Scenario", "Walkthrough", "Framework", "Prompt System", "Ready-to-Use Output"].map((t) => (
                <span key={t} className="px-3 py-1.5 rounded-full bg-[#0F172A] text-white text-xs font-bold">{t}</span>
              ))}
            </div>
          </FadeUp>
        </div>
      </section>

      {/* CURRICULUM */}
      <section id="curriculum" className="py-16 sm:py-24 bg-[#F9FAFB] border-y border-[#E5E7EB]" data-testid="ai-curriculum">
        <div className="max-w-4xl mx-auto px-5 sm:px-8">
          <FadeUp>
            <div className="text-xs text-[#C9A227] tracking-[0.3em] font-bold mb-3">CURRICULUM</div>
            <h2 className="font-black text-2xl sm:text-4xl text-[#0F172A] mb-3 leading-snug">
              What You&apos;ll Learn Inside AI Work Upgrade
            </h2>
            <p className="text-base text-[#6B7280] mb-12">
              5 practical modules built around real workplace moments — not abstract AI theory.
            </p>
          </FadeUp>

          <div className="space-y-6">
            {MODULES.map((m, i) => (
              <FadeUp key={m.n} delay={i * 40}>
                <div
                  className="bg-white border border-[#E5E7EB] rounded-2xl p-6 sm:p-9 hover:border-[#0F172A]/30 hover:shadow-[0_15px_40px_-20px_rgba(15,23,42,0.2)] transition-all"
                  data-testid={`ai-curriculum-module-${m.n}`}
                >
                  <div className="flex items-start gap-5 sm:gap-7 pb-6 mb-6 border-b border-[#E5E7EB]">
                    <div className="shrink-0">
                      <div className="text-[#C9A227] font-mono text-2xl sm:text-3xl font-black">{m.n}</div>
                      <div className="text-[10px] text-[#6B7280] tracking-widest mt-1">MODULE</div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg sm:text-2xl font-black text-[#0F172A] leading-snug">{m.title}</h3>
                    </div>
                  </div>

                  <div className="mb-5">
                    <div className="flex items-center gap-2 mb-2">
                      <Target className="w-3.5 h-3.5 text-[#0F766E]" strokeWidth={2.5} />
                      <span className="text-[11px] font-bold tracking-widest text-[#0F766E]">SCENARIO</span>
                    </div>
                    <p className="text-[15px] sm:text-base text-[#374151] leading-relaxed pl-5">{m.scenario}</p>
                  </div>

                  <div className="mb-5">
                    <div className="flex items-center gap-2 mb-3">
                      <Zap className="w-3.5 h-3.5 text-[#C9A227]" strokeWidth={2.5} />
                      <span className="text-[11px] font-bold tracking-widest text-[#C9A227]">YOU WILL LEARN TO BUILD</span>
                    </div>
                    <ul className="grid sm:grid-cols-2 gap-2 pl-5">
                      {m.learn.map((l) => (
                        <li key={l} className="flex items-center gap-2 text-sm sm:text-[15px] text-[#0F172A]">
                          <span className="w-1.5 h-1.5 rounded-full bg-[#C9A227] shrink-0" />
                          {l}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-3 pt-4 border-t border-[#E5E7EB] mb-5">
                    <div className="bg-[#0F172A] text-white rounded-lg p-4">
                      <div className="text-[10px] text-white/60 tracking-widest mb-1.5">FRAMEWORK</div>
                      <div className="font-bold text-sm sm:text-[15px]">{m.framework}</div>
                    </div>
                    <div className="bg-[#0F766E]/8 border border-[#0F766E]/20 rounded-lg p-4">
                      <div className="text-[10px] text-[#0F766E] tracking-widest font-bold mb-1.5">OUTPUT</div>
                      <div className="text-sm sm:text-[15px] text-[#0F172A] leading-relaxed">{m.output}</div>
                    </div>
                  </div>

                  <div className="bg-[#FFFBEB] border-l-2 border-[#C9A227] px-4 py-3 rounded-r-md">
                    <div className="text-[10px] text-[#C9A227] tracking-widest font-bold mb-1 flex items-center gap-1.5">
                      <AlertCircle className="w-3 h-3" /> WHY IT MATTERS
                    </div>
                    <p className="text-sm text-[#0F172A] leading-relaxed">{m.why}</p>
                  </div>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* BONUSES */}
      <section className="py-16 sm:py-24 bg-gradient-to-b from-white via-[#FBF9F4] to-white" data-testid="ai-bonus">
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <FadeUp>
            <div className="text-xs text-[#C9A227] tracking-[0.3em] font-bold mb-3">INCLUDED</div>
            <h2 className="font-black text-2xl sm:text-4xl text-[#0F172A] mb-3 leading-snug">Included Bonuses</h2>
            <p className="text-sm sm:text-base text-[#6B7280] mb-12" data-testid="ai-bonus-included-line">
              <span className="font-bold text-[#0F172A]">Included with your {PRICE_LABEL} access.</span> No upsells.
            </p>
          </FadeUp>
          <div className="grid md:grid-cols-3 gap-5">
            {BONUSES.map((b, i) => (
              <FadeUp key={b.title} delay={i * 90}>
                <div className="bg-white border border-[#E5E7EB] rounded-xl p-6 sm:p-7 h-full" data-testid={`ai-bonus-card-${i}`}>
                  <div className="w-10 h-10 rounded-md bg-[#C9A227]/12 flex items-center justify-center mb-4">
                    <Sparkles className="w-5 h-5 text-[#C9A227]" />
                  </div>
                  <div className="text-[10px] text-[#C9A227] tracking-widest font-bold mb-1.5">BONUS {String(i + 1).padStart(2, "0")}</div>
                  <h3 className="text-lg font-black text-[#0F172A] mb-3 leading-snug">{b.title}</h3>
                  <p className="text-sm text-[#374151] leading-relaxed">{b.desc}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* WHO FOR / NOT FOR */}
      <section className="py-16 sm:py-24 bg-[#F9FAFB] border-y border-[#E5E7EB]" data-testid="ai-audience">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 grid md:grid-cols-2 gap-7">
          <FadeUp>
            <div className="bg-white border border-[#0F766E]/25 rounded-xl p-7 sm:p-8 h-full" data-testid="ai-for-card">
              <div className="text-xs text-[#0F766E] tracking-[0.25em] font-bold mb-3">FOR</div>
              <h3 className="font-black text-xl text-[#0F172A] mb-5">Who This Course Is For</h3>
              <ul className="space-y-3">
                {[
                  "You use AI but feel the output is still too generic",
                  "You want your work to sound more structured and senior",
                  "You write reports, emails, updates, or recommendations",
                  "You attend meetings where you need to contribute clearly",
                  "You want to improve your output without becoming technical",
                  "You want AI to help you think better, not just write faster",
                ].map((t) => (
                  <li key={t} className="flex items-start gap-3 text-[15px] text-[#0F172A]">
                    <Check className="w-4 h-4 mt-0.5 text-[#0F766E] shrink-0" strokeWidth={3} />
                    {t}
                  </li>
                ))}
              </ul>
            </div>
          </FadeUp>
          <FadeUp delay={100}>
            <div className="bg-white border border-[#E5E7EB] rounded-xl p-7 sm:p-8 h-full" data-testid="ai-notfor-card">
              <div className="text-xs text-[#6B7280] tracking-[0.25em] font-bold mb-3">NOT FOR</div>
              <h3 className="font-black text-xl text-[#0F172A] mb-5">Who This Is Not For</h3>
              <ul className="space-y-3">
                {[
                  "You want to learn coding",
                  "You want a technical AI course",
                  "You want a generic list of prompts",
                  "You want a shortcut that does all the thinking for you",
                  "You are looking for hype instead of practical workplace systems",
                ].map((t) => (
                  <li key={t} className="flex items-start gap-3 text-[15px] text-[#6B7280]">
                    <X className="w-4 h-4 mt-0.5 text-[#9CA3AF] shrink-0" strokeWidth={3} />
                    {t}
                  </li>
                ))}
              </ul>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* VALUE / PRICING */}
      <section className="py-16 sm:py-24" data-testid="ai-value">
        <div className="max-w-4xl mx-auto px-5 sm:px-8">
          <FadeUp>
            <div className="text-xs text-[#C9A227] tracking-[0.3em] font-bold mb-3">THE VALUE</div>
            <h2 className="font-black text-2xl sm:text-4xl text-[#0F172A] mb-6 leading-snug">
              Why {PRICE_LABEL} Is a Simple Decision
            </h2>
            <ul className="space-y-3 mb-10 text-base sm:text-lg text-[#374151]">
              {[
                "One strong meeting contribution can change how people see you.",
                "One better report can make your thinking more visible.",
                "One calm response to a sensitive email can protect your reputation.",
                "One stronger recommendation can make you sound more prepared than your peers.",
              ].map((t) => (
                <li key={t} className="flex items-start gap-3">
                  <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#C9A227] shrink-0" />
                  {t}
                </li>
              ))}
            </ul>
            <p className="text-base sm:text-lg text-[#0F172A] font-semibold mb-10">
              AI Work Upgrade gives you practical systems you can reuse every week.
            </p>

            <div className="bg-[#0F172A] text-white rounded-2xl p-7 sm:p-10 text-center relative overflow-hidden">
              <div
                aria-hidden
                className="absolute inset-0 opacity-[0.07] pointer-events-none"
                style={{
                  backgroundImage:
                    "radial-gradient(circle at 80% 20%, #C9A227 0%, transparent 50%)",
                }}
              />
              <div className="relative">
                <div className="text-[10px] tracking-[0.3em] text-[#C9A227] font-bold mb-3">AI WORK UPGRADE</div>
                <div className="flex items-baseline justify-center gap-3 mb-3 flex-wrap">
                  <span className="text-white/40 line-through text-lg">Regular {ANCHOR_LABEL}</span>
                  <span className="font-black text-5xl sm:text-6xl text-[#C9A227]">Today {PRICE_LABEL}</span>
                </div>
                <div className="text-[12px] text-white/55 mb-1.5" data-testid="ai-value-aed">Approx. {PRICE_LABEL_AED} · one-time payment, no subscription</div>
                <div className="text-white/70 text-sm mb-7">Instant digital access · Self-paced · No technical background required</div>

                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-left text-sm text-white/90 max-w-md mx-auto mb-8">
                  {[
                    "5 practical modules",
                    "50-prompt library",
                    "Work templates",
                    "Before/after examples",
                  ].map((t) => (
                    <li key={t} className="flex items-center gap-2">
                      <Check className="w-3.5 h-3.5 text-[#C9A227]" strokeWidth={3} />
                      {t}
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => startCheckout("value")}
                  disabled={loading}
                  data-testid="ai-value-buy-btn"
                  className="w-full sm:w-auto bg-[#C9A227] hover:bg-[#b78d1b] text-[#0F172A] font-black text-base px-10 py-4 rounded-md transition-all shadow-[0_15px_40px_rgba(201,162,39,0.35)] disabled:opacity-50 inline-flex items-center justify-center gap-2"
                >
                  <span>Get Instant Access — {PRICE_LABEL}</span>
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* TRUST / RISK REVERSAL */}
      <section className="py-16 sm:py-24 bg-[#F9FAFB] border-y border-[#E5E7EB]" data-testid="ai-trust">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 text-center">
          <FadeUp>
            <div className="text-xs text-[#C9A227] tracking-[0.3em] font-bold mb-3">TRUST</div>
            <h2 className="font-black text-2xl sm:text-4xl text-[#0F172A] mb-6 leading-snug">
              Built for Real Work, Not AI Hype
            </h2>
            <p className="text-base sm:text-lg text-[#374151] leading-relaxed mb-3">
              AI Work Upgrade is practical, concise, and scenario-based.
            </p>
            <p className="text-base sm:text-lg text-[#374151] leading-relaxed mb-8">
              You will not spend hours learning technical features. You will learn workflows you can apply the next time:
            </p>
            <ul className="space-y-2.5 max-w-md mx-auto text-left text-[15px] text-[#0F172A] mb-10">
              {[
                "Your manager asks for your opinion",
                "You prepare for a meeting",
                "You write a report",
                "You respond to a sensitive email",
                "You review work before sending it",
              ].map((t) => (
                <li key={t} className="flex items-start gap-3">
                  <Check className="w-4 h-4 mt-0.5 text-[#0F766E] shrink-0" strokeWidth={3} />
                  {t}
                </li>
              ))}
            </ul>
            <div className="flex items-center justify-center gap-5 flex-wrap text-xs text-[#6B7280] font-semibold tracking-wide">
              <span>Instant access</span>
              <span className="w-1 h-1 rounded-full bg-[#D1D5DB]" />
              <span>Self-paced</span>
              <span className="w-1 h-1 rounded-full bg-[#D1D5DB]" />
              <span>No technical background required</span>
              <span className="w-1 h-1 rounded-full bg-[#D1D5DB]" />
              <span>Templates included</span>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 sm:py-24" data-testid="ai-faq">
        <div className="max-w-3xl mx-auto px-5 sm:px-8">
          <FadeUp>
            <div className="text-xs text-[#C9A227] tracking-[0.3em] font-bold mb-3">QUESTIONS</div>
            <h2 className="font-black text-2xl sm:text-4xl text-[#0F172A] mb-10 leading-snug">Frequently Asked Questions</h2>
          </FadeUp>
          <FadeUp delay={80}>
            <Accordion type="single" collapsible className="space-y-3">
              {FAQS.map((f, i) => (
                <AccordionItem
                  key={i}
                  value={`faq-${i}`}
                  className="bg-white border border-[#E5E7EB] rounded-xl px-5 data-[state=open]:border-[#0F172A]/30"
                  data-testid={`ai-faq-${i}`}
                >
                  <AccordionTrigger className="text-left text-[15px] sm:text-base font-bold text-[#0F172A] hover:no-underline py-5">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-left text-sm text-[#374151] leading-relaxed pb-5">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </FadeUp>
        </div>
      </section>

      {/* GUARANTEE — calm, trustworthy risk reversal */}
      <section className="py-14 sm:py-16" data-testid="ai-guarantee-section">
        <div className="max-w-3xl mx-auto px-5 sm:px-8">
          <div className="bg-white border border-[#0F766E]/30 rounded-2xl p-6 sm:p-8 flex flex-col sm:flex-row items-center gap-5 sm:gap-7 shadow-[0_15px_50px_-25px_rgba(15,118,110,0.3)]">
            <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-[#0F766E]/8 border border-[#0F766E]/25 flex items-center justify-center shrink-0">
              <ShieldCheck className="w-9 h-9 sm:w-10 sm:h-10 text-[#0F766E]" strokeWidth={1.5} />
            </div>
            <div className="text-center sm:text-left">
              <h3 className="font-black text-lg sm:text-xl text-[#0F172A] leading-tight mb-2">
                14-Day Money-Back Guarantee
              </h3>
              <p className="text-[14px] sm:text-[15px] text-[#374151] leading-relaxed">
                Try AI Work Upgrade. If it does not help you improve how you use AI at work,
                request a refund within 14 days.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-20 sm:py-28 bg-[#0F172A] text-white relative overflow-hidden" data-testid="ai-final-cta">
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none opacity-[0.06]"
          style={{
            backgroundImage:
              "radial-gradient(circle at 10% 20%, #C9A227 0%, transparent 40%), radial-gradient(circle at 90% 80%, #C9A227 0%, transparent 40%)",
          }}
        />
        <div className="relative max-w-3xl mx-auto px-5 sm:px-8 text-center">
          <FadeUp>
            <h2 className="font-black text-3xl sm:text-5xl leading-[1.15] mb-6 tracking-tight">
              Don&apos;t Use AI to Replace Your Thinking.
              <br />
              <span className="text-[#C9A227]">Use It to Upgrade It.</span>
            </h2>
            <p className="text-white/75 text-base sm:text-lg leading-relaxed mb-10 max-w-2xl mx-auto">
              AI Work Upgrade gives you practical systems for the workplace moments that shape how
              professional, prepared, and strategic you appear.
            </p>
            <div className="inline-flex flex-col items-center gap-4">
              <div className="flex flex-col items-center gap-1">
                <div className="flex items-baseline gap-3 flex-wrap justify-center">
                  <span className="text-white/50 line-through text-lg">Regular {ANCHOR_LABEL}</span>
                  <span className="text-[#C9A227] font-black text-3xl sm:text-4xl">Launch {PRICE_LABEL}</span>
                </div>
                <span className="text-[12px] text-white/55">Approx. {PRICE_LABEL_AED} · one-time payment, no subscription</span>
              </div>
              <button
                onClick={() => startCheckout("final")}
                disabled={loading}
                data-testid="ai-final-buy-btn"
                className="bg-[#C9A227] hover:bg-[#b78d1b] text-[#0F172A] font-black text-base sm:text-lg px-10 py-5 rounded-md transition-all shadow-[0_15px_40px_rgba(201,162,39,0.35)] disabled:opacity-50 flex items-center gap-2"
              >
                <span>Get Instant Access — {PRICE_LABEL}</span>
                <ArrowRight className="w-4 h-4" />
              </button>
              <div className="text-xs text-white/60 mt-1">
                Instant access · 50 prompts · Work templates · 14-day money-back guarantee
              </div>
              <div className="flex items-center gap-2 text-[11px] text-white/50 mt-1 text-center max-w-xs">
                <Lock className="w-3 h-3 shrink-0" />
                <span>Secure checkout. Pay by card, Apple Pay, Google Pay or supported wallet methods.</span>
              </div>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-7 border-t border-[#E5E7EB] bg-white" data-testid="ai-footer">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-[#6B7280]">
          <div className="flex items-center gap-2">
            <Sparkles className="w-3.5 h-3.5 text-[#C9A227]" />
            <span>AI Work Upgrade · © {new Date().getFullYear()} · Taught by Jackie Escalera</span>
          </div>
          <a
            href={LINKEDIN_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackCtaClick("ai-footer-linkedin", "landing-ai")}
            className="hover:text-[#0F172A] transition-colors flex items-center gap-1.5"
          >
            <Linkedin className="w-3.5 h-3.5" /> LinkedIn
          </a>
        </div>
      </footer>

      {/* Desktop sticky CTA bar — slim, appears after the user scrolls past the hero */}
      <div
        className={`hidden sm:flex fixed bottom-5 left-1/2 -translate-x-1/2 z-40 items-center gap-5 px-5 py-3 rounded-full bg-[#0F172A] text-white shadow-[0_15px_50px_-10px_rgba(15,23,42,0.55)] border border-white/10 transition-all duration-300 ${
          stickyVisible ? "opacity-100 translate-y-0 pointer-events-auto" : "opacity-0 translate-y-3 pointer-events-none"
        }`}
        data-testid="ai-desktop-sticky-cta"
      >
        <div className="flex items-center gap-2 pr-1">
          <Sparkles className="w-4 h-4 text-[#C9A227]" />
          <span className="font-bold text-sm tracking-tight">AI Work Upgrade</span>
        </div>
        <div className="h-5 w-px bg-white/20" aria-hidden />
        <div className="flex items-baseline gap-2 text-sm">
          <span className="text-white/45 line-through text-xs">Reg {ANCHOR_LABEL}</span>
          <span className="text-[#C9A227] font-black">Launch {PRICE_LABEL}</span>
        </div>
        <button
          onClick={() => startCheckout("desktop-sticky")}
          disabled={loading}
          data-testid="ai-desktop-sticky-buy-btn"
          className="bg-[#C9A227] hover:bg-[#b78d1b] text-[#0F172A] font-bold text-sm px-5 py-2.5 rounded-full flex items-center gap-1.5 transition-colors disabled:opacity-50 min-h-[48px]"
        >
          Get Instant Access
          <ArrowRight className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Mobile sticky CTA */}
      <div
        className="sm:hidden fixed bottom-0 inset-x-0 z-40 bg-white border-t border-[#E5E7EB] px-4 py-3 flex items-center gap-3"
        style={{ boxShadow: "0 -8px 24px rgba(15,23,42,0.08)" }}
        data-testid="ai-sticky-cta"
      >
        <div className="flex-1 min-w-0">
          <div className="text-[10px] text-[#6B7280] tracking-wide line-through">Regular {ANCHOR_LABEL}</div>
          <div className="text-base font-black text-[#0F172A] leading-tight">Launch {PRICE_LABEL}</div>
        </div>
        <button
          onClick={() => startCheckout("sticky")}
          disabled={loading}
          className="bg-[#0F172A] hover:bg-[#1E293B] text-white font-bold text-sm px-5 py-3 rounded-md flex items-center gap-1.5 disabled:opacity-50"
          data-testid="ai-sticky-buy-btn"
        >
          Get Instant Access
          <ArrowRight className="w-3.5 h-3.5" />
        </button>
      </div>

      <div className="sm:hidden h-20" />
    </div>
  );
}
